import React, { useState } from "react";
import { Plus, RotateCw } from "lucide-react";
import { finApi, money } from "@/lib/financeApi";
import { btnGold, Chip, Empty, ErrorBox, INVOICE_CHIP, Loading, useLoad } from "@/components/nds/windows/finance/parts";
import { applyFilters, InvoiceFilters, STATUS_TABS, StatusTabs } from "@/components/nds/windows/finance/InvoiceTabs";
import InvoiceActions from "@/components/nds/windows/finance/InvoiceActions";
import InvoiceForm from "@/components/nds/windows/finance/InvoiceForm";
import LaborInvoiceForm from "@/components/nds/windows/finance/LaborInvoiceForm";

export const FLOW_CHIP = { receivable: ["orange", "Billed by TEA"], payable: ["blue", "Rent to landlord"] };

export function InvoiceRow({ inv, onOpen, onChanged }) {
  const [tone, label] = INVOICE_CHIP[inv.status];
  const flow = FLOW_CHIP[inv.flow];
  return (
    <li data-testid={`fin-inv-row-${inv.id}`} onClick={() => onOpen(inv.id)} className="px-5 py-3 space-y-2 cursor-pointer hover:bg-nex-gold/5 transition-colors">
      <div className="flex items-center gap-3">
        <div className="flex flex-wrap items-center gap-3 min-w-0 flex-1">
        <span className="text-nex-silver font-medium truncate max-w-[200px]">{inv.vendor_name}</span>
        <span className="font-mono text-[10px] text-nex-muted">{inv.invoice_number}</span>
        <span className="font-mono text-[10px] text-nex-muted">{inv.period_start} → {inv.period_end}</span>
        <Chip tone={tone} data-testid={`fin-inv-status-${inv.id}`}>{inv.legacy_import && inv.status === "approved" ? "Awaiting payment" : label}</Chip>
        {flow && <Chip tone={flow[0]} data-testid={`fin-inv-flow-${inv.id}`}>{flow[1]}</Chip>}
        {inv.legacy_import && <Chip tone="grey" data-testid={`fin-inv-legacy-${inv.id}`}>Old platform</Chip>}
        </div>
        <span className="shrink-0 text-right">
          <span className="block font-mono text-[9px] tracking-[0.18em] uppercase text-nex-muted">{inv.status === "paid" ? "Paid" : "Pays"}</span>
          <span data-testid={`fin-inv-paydate-${inv.id}`} className="font-mono text-xs text-nex-gold">{inv.pay_date}</span>
          {inv.expected_total > 0 && <span data-testid={`fin-inv-expected-${inv.id}`} className="font-mono text-[10px] text-sky-200">{inv.expected_posted} of {inv.expected_total} posted</span>}
        </span>
        <span className="w-28 shrink-0 text-right">
          <span className="block font-mono text-sm text-nex-silver">{money(inv.total_amount)}</span>
          <span data-testid={`fin-inv-balance-${inv.id}`} className="font-mono text-[10px] text-nex-muted">{inv.balance_due < 0 ? `${money(-inv.balance_due)} margin` : `${money(inv.balance_due)} due`}</span>
        </span>
      </div>
      <InvoiceActions inv={inv} onChanged={onChanged} />
    </li>
  );
}

const kept = { tab: "all", f: { flow: "", cycle: "", q: "" } };

export default function InvoicesView({ onOpen }) {
  const [adding, setAdding] = useState(false);
  const [tab, setTabState] = useState(kept.tab);
  const [f, setFState] = useState(kept.f);
  const setTab = (t) => { kept.tab = t; setTabState(t); };
  const setF = (fn) => setFState((x) => { kept.f = typeof fn === "function" ? fn(x) : fn; return kept.f; });
  const [state, load] = useLoad(() => finApi.get("/invoices"), []);
  const filtered = applyFilters(state.data?.invoices || [], f);
  const list = filtered.filter(STATUS_TABS.find(([id]) => id === tab)[2]);
  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="px-5 py-3 flex flex-wrap items-center gap-2 border-b border-white/5">
        {!adding && <button type="button" onClick={() => setAdding("labor")} data-testid="fin-inv-new-labor" className={`${btnGold} inline-flex items-center gap-1.5`}><Plus className="h-3 w-3" /> Resident labor invoice</button>}
        {!adding && <button type="button" onClick={() => setAdding(true)} data-testid="fin-inv-new" className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 rounded-sm font-mono text-[10px] uppercase tracking-[0.16em] text-nex-silver hover:border-nex-gold"><Plus className="h-3 w-3" /> Other invoice</button>}
        <InvoiceFilters invoices={state.data?.invoices || []} f={f} setF={setF} />
        <span data-testid="fin-inv-count" className="font-mono text-[10px] text-nex-muted">{list.length} shown</span>
        <button type="button" onClick={load} data-testid="fin-inv-refresh" aria-label="Refresh" className="ml-auto p-1.5 text-nex-muted hover:text-nex-gold"><RotateCw className="h-3.5 w-3.5" /></button>
      </div>
      {state.data && <div className="px-5 py-3 border-b border-white/5"><StatusTabs invoices={filtered} tab={tab} setTab={setTab} /></div>}
      {adding === "labor" && <div className="px-5 pt-3"><LaborInvoiceForm onCancel={() => setAdding(false)} onDone={(id) => { setAdding(false); load(); onOpen(id); }} /></div>}
      {adding === true && <div className="px-5 pt-3"><InvoiceForm onCancel={() => setAdding(false)} onDone={(id) => { setAdding(false); onOpen(id); }} /></div>}
      {state.loading && !state.data ? <Loading testid="fin-inv-loading" label="Loading invoices…" />
        : state.error ? <ErrorBox testid="fin-inv-error" error={state.error} onRetry={load} />
        : list.length ? (
          <ul data-testid="fin-inv-list" className="flex-1 overflow-y-auto divide-y divide-white/5">
            {[...list].reverse().map((inv) => <InvoiceRow key={inv.id} inv={inv} onOpen={onOpen} onChanged={load} />)}
          </ul>
        ) : <Empty testid="fin-inv-empty">No invoices match.</Empty>}
    </div>
  );
}
